import type { StateCreator } from "zustand/vanilla";

/**
 * EmbedTarget is the resource the embed wizard is open for: an itinerary or a
 * collection, never both and never neither. A union rather than two optional fields,
 * so an impossible target cannot be spelled at all — the same shape a share link's
 * input takes. The wizard carries only the pointer; everything else it needs (the org
 * default props, the publishable key, the live config) it reads from server state and
 * its own local form, so the store slot stays a minimal pointer rather than a mirror
 * of the whole wizard.
 */
export type EmbedTarget =
  | {
      /** collectionId is the collection the embed snippet is being authored for. */
      collectionId: string;
      itineraryId?: never;
    }
  | {
      collectionId?: never;
      /** itineraryId is the itinerary the embed snippet is being authored for. */
      itineraryId: string;
    };

/**
 * EmbedSliceState is the open embed-wizard slot.
 */
export interface EmbedSliceState {
  /**
   * embed is the open embed wizard state (null = the wizard is closed). It holds
   * only the resource the snippet is authored for; the wizard's config form is
   * local to the surface, so this slot stays a minimal open/closed pointer. Set
   * only through the tool bus, like every other UiAction.
   */
  embed: EmbedTarget | null;
}

/**
 * EmbedSliceActions is the whole-slot setter that drives the embed wizard,
 * reached only through the tool bus.
 */
export interface EmbedSliceActions {
  /**
   * setEmbed opens the embed wizard with the given target, or closes it when
   * passed null. The whole-slot setter the open/close tools drive; the wizard has
   * no narrower fields in the store (its config lives in the surface's local
   * form), so this is the slice's only action.
   */
  setEmbed: (embed: EmbedTarget | null) => void;
}

/**
 * embedInitialState seeds the embed wizard slot closed.
 */
export const embedInitialState: EmbedSliceState = {
  embed: null,
};

/**
 * createEmbedSlice builds the embed-wizard action. The single whole-slot setter
 * opens the wizard for a target or closes it, mirroring the video export slice's
 * open/close shape without the wizard-progress fields the embed surface keeps in its
 * own local form.
 */
export const createEmbedSlice: StateCreator<
  EmbedSliceState & EmbedSliceActions,
  [],
  [],
  EmbedSliceActions
> = (set) => ({
  setEmbed: (embed) => set({ embed }),
});

/**
 * selectEmbed is the open embed wizard state, or null when the wizard is closed.
 * The mount-guard selector an embed surface reads to decide whether to render at
 * all — narrow so the guard re-renders only on the open/close flip, mirroring
 * selectVideoExport.
 */
export const selectEmbed = (state: EmbedSliceState): EmbedTarget | null => state.embed;
